import React from "react";
import { DollarSign, TrendingUp, Globe, CreditCard, Download } from "lucide-react";
import { SummaryCard } from "./SummaryCard";
import { CountrySummary, PaymentMethodSummary, GlobalSummary } from "../../../../services/profitTrackingService";
import { formatNumber } from "../utils/profitFormatters";

interface OverviewTabProps {
  globalSummary: GlobalSummary | null;
  countrySummary: CountrySummary[];
  paymentMethodSummary: PaymentMethodSummary[];
  onDownload: (type: "entries" | "country" | "payment" | "daily") => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  globalSummary,
  countrySummary,
  paymentMethodSummary,
  onDownload,
}) => {
  return (
    <div className="space-y-6">
      {/* Global summary cards */}
      {globalSummary && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              icon={<DollarSign size={20} />}
              label="Capital Total"
              value={`${formatNumber(globalSummary.total_capital)} CAD`}
              color="blue"
            />
            <SummaryCard
              icon={<TrendingUp size={20} />}
              label="Revenu Net"
              value={`${formatNumber(globalSummary.total_net_profit)} XAF`}
              color="emerald"
            />
            <SummaryCard
              icon={<CreditCard size={20} />}
              label="Dépenses Totales"
              value={`${formatNumber(globalSummary.total_expenses)} XAF`}
              color="red"
            />
            <SummaryCard
              icon={<TrendingUp size={20} />}
              label="Bénéfice Final (CAD)"
              value={`${formatNumber(globalSummary.total_final_profit_cad)} CAD`}
              color="purple"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              icon={<TrendingUp size={20} />}
              label="Revenu Brut"
              value={`${formatNumber(globalSummary.total_gross_profit)} XAF`}
              color="green"
            />
            <SummaryCard
              icon={<CreditCard size={20} />}
              label="Frais (MTN, etc.)"
              value={`${formatNumber(globalSummary.total_fees)} XAF`}
              color="red"
            />
            <SummaryCard
              icon={<DollarSign size={20} />}
              label="Revenu en CAD"
              value={`${formatNumber(globalSummary.total_profit_cad)} CAD`}
              color="purple"
            />
            <SummaryCard
              icon={<TrendingUp size={20} />}
              label="Bénéfice Final (XAF)"
              value={`${formatNumber(globalSummary.total_final_profit)} XAF`}
              color="emerald"
            />
          </div>
        </>
      )}

      {/* By Country */}
      <div className="p-6 bg-white rounded-xl border">
        <div className="flex justify-between items-center mb-4">
          <h2 className="flex gap-2 items-center text-lg font-semibold text-gray-800">
            <Globe size={20} className="text-blue-600" /> Bénéfices par Pays
          </h2>
          <button
            onClick={() => onDownload("country")}
            className="flex gap-1 items-center px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg border hover:bg-gray-200"
          >
            <Download size={14} /> Excel
          </button>
        </div>
        {countrySummary.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune donnée</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-4 py-3 font-medium text-left text-gray-600">Pays</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Capital</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Revenu Brut</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Frais</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Revenu Net</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Revenu (CAD)</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Dépenses</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Bén. Final</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Bén. Final (CAD)</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Opérations</th>
                </tr>
              </thead>
              <tbody>
                {countrySummary.map((c) => (
                  <tr key={c.country} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{c.country}</td>
                    <td className="px-4 py-3 text-right">{formatNumber(c.total_capital)}</td>
                    <td className="px-4 py-3 text-right text-green-600">{formatNumber(c.total_gross_profit)}</td>
                    <td className="px-4 py-3 text-right text-red-500">{formatNumber(c.total_fees)}</td>
                    <td className="px-4 py-3 font-semibold text-right text-emerald-600">
                      {formatNumber(c.total_net_profit)}
                    </td>
                    <td className="px-4 py-3 font-semibold text-right text-purple-600">
                      {formatNumber(c.total_profit_cad)}
                    </td>
                    <td className="px-4 py-3 text-right text-orange-500">{formatNumber(c.total_expenses)}</td>
                    <td className="px-4 py-3 font-bold text-right text-emerald-700">
                      {formatNumber(c.total_final_profit)}
                    </td>
                    <td className="px-4 py-3 font-bold text-right text-purple-700">
                      {formatNumber(c.total_final_profit_cad)}
                    </td>
                    <td className="px-4 py-3 text-right">{c.operation_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* By Payment Method */}
      <div className="p-6 bg-white rounded-xl border">
        <div className="flex justify-between items-center mb-4">
          <h2 className="flex gap-2 items-center text-lg font-semibold text-gray-800">
            <CreditCard size={20} className="text-orange-600" /> Bénéfices par Moyen de Paiement
          </h2>
          <button
            onClick={() => onDownload("payment")}
            className="flex gap-1 items-center px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg border hover:bg-gray-200"
          >
            <Download size={14} /> Excel
          </button>
        </div>
        {paymentMethodSummary.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune donnée</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-4 py-3 font-medium text-left text-gray-600">Moyen de paiement</th>
                  <th className="px-4 py-3 font-medium text-left text-gray-600">Pays</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Capital</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Revenu Brut</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Frais</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Revenu Net</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Revenu (CAD)</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Dépenses</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Bén. Final</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Bén. Final (CAD)</th>
                  <th className="px-4 py-3 font-medium text-right text-gray-600">Opérations</th>
                </tr>
              </thead>
              <tbody>
                {paymentMethodSummary.map((pm, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 rounded-full">
                        {pm.payment_method}
                      </span>
                    </td>
                    <td className="px-4 py-3">{pm.country}</td>
                    <td className="px-4 py-3 text-right">{formatNumber(pm.total_capital)}</td>
                    <td className="px-4 py-3 text-right text-green-600">{formatNumber(pm.total_gross_profit)}</td>
                    <td className="px-4 py-3 text-right text-red-500">{formatNumber(pm.total_fees)}</td>
                    <td className="px-4 py-3 font-semibold text-right text-emerald-600">
                      {formatNumber(pm.total_net_profit)}
                    </td>
                    <td className="px-4 py-3 font-semibold text-right text-purple-600">
                      {formatNumber(pm.total_profit_cad)}
                    </td>
                    <td className="px-4 py-3 text-right text-orange-500">{formatNumber(pm.total_expenses)}</td>
                    <td className="px-4 py-3 font-bold text-right text-emerald-700">
                      {formatNumber(pm.total_final_profit)}
                    </td>
                    <td className="px-4 py-3 font-bold text-right text-purple-700">
                      {formatNumber(pm.total_final_profit_cad)}
                    </td>
                    <td className="px-4 py-3 text-right">{pm.operation_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
